import STUtils from "../../assets/styles/utility.module.scss";
import STMemberEditForm from "../../assets/styles/organisms/memberEditForm.module.scss";
import type { MemberEditForm as TMemberEditForm } from "../../types/form/memberEditForm";
import { useEffect, useRef, useState } from "react";
import Button from "../atoms/Button";
import STButton from "../../assets/styles/atoms/button.module.scss";
import type TApiResult from "../../types/api/TApiResult";
import AlartMessageBox from "../atoms/AlartMessageBox";
import {
  fetchMemberDetail,
  type TApiMemberDetailResult,
} from "../../utils/api/fetchMemberDetail";
import {
  postMemberUpdate,
  type TApiMemberUpdateValidateErrors,
} from "../../utils/api/postMemberUpdate";
import SingleTextRow from "../molecules/formTableRow/SingleTextRow";
import {
  MEMBER_EDIT_FORM_ITEM,
  type TAppMemberEditFormItem,
} from "../../types/TAppMemberEditFormItem";
import type { TAppFormError } from "../../types/TAppFormError";
import SinglePasswordRow from "../molecules/formTableRow/SinglePasswordRow";
import { useHistory, useLocation } from "react-router-dom";
import SingleTextWithIconRow from "../molecules/formTableRow/SingleTextWithIconRow";
import SingleCheckBoxRow from "../molecules/formTableRow/SingleCheckBoxRow";
import MultiPullDownRow from "../molecules/formTableRow/MultiPullDownRow";
import { routes } from "../../router/Router";
import { postMemberAccessLog } from "../../utils/api/postMemberAccessLog";
import { ACCESS_LOG_ACTION } from "../../types/TAppAccessLogAction";
import { years, months, days } from "../atoms/SelectDate";
import filterMessageFormError from "../../utils/form/filterMessageFormError";
import { Helmet } from "react-helmet";
import CONSTANTS from "../../utils/constants";
import { EMAIL_PATTERN } from "../../utils/emailValidation";
import SingleCheckBox from "../molecules/formTableRow/SingleCheckBox";
import TableRow from "../atoms/TableRow";
import PushNotificationSettingModal from "../organisms/PushNotificationSettingModal";
import { NotificationsNone } from "@material-ui/icons";
import { Popover } from "@material-ui/core";
import { useIsMobile } from "../../hooks/useIsMobile";
import isPWA from "../../utils/checkPWA";
import AccountIcon from "../atoms/icons/AccountIcon";
import AvatarSelectDialog from "../organisms/AvatarSelectDialog";
import { postImage } from "../../utils/api/postImage";
import { getCommonMemberIcon } from "../../utils/api/getICommonMemberIcon";
import { getSubdomain } from "../../utils/getSubdomain";
import { fetchNGWords } from "../../utils/api/fetchNGWords";
import type { GetNGWordsResponse } from "../../api/ng_words";
import AlartMessages from "../atoms/AlartMessages";

const MAX_FILE_SIZE = 10 * 1024 * 1024; //10MB

type TmpPassword = {
  existPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
};

const MemberEditForm: React.FC = () => {
  // const defaultYear = new Date().getFullYear() - 30; // 30年前を初期値
  // const defaultDate = new Date(defaultYear, 0, 1);

  const initial: TMemberEditForm = {
    tel: "",
    email: "",
    birthday: undefined,
    instagramOwnerinput: "",
    line: 0,
    existPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
    mail_not_recommended: 0,
    line_not_recommended: 0,
    allow_chat_mail_sending: 0,
    nickname: "",
    avatar_url: "",
    avatarType: "none",
  };
  const [formErrors, setFormErrors] = useState<
    TAppFormError<TAppMemberEditFormItem>[]
  >([]);

  const [tmpPassword, setTmpPassword] = useState<TmpPassword>({
    existPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
  });
  type IconItem = {
    file_url: string;
    presign_url?: string;
  };
  const { isMobile } = useIsMobile();
  const subdomain = getSubdomain(window.location.hostname);
  const [form, setForm] = useState(initial);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [name, setName] = useState("");
  const [nameKana, setNameKana] = useState("");
  const [canApply, setCanApply] = useState(false);
  const history = useHistory();
  const [isShownUnlinkLine, setIsShownUnlinkLine] = useState(false);
  const [lastConfirmedAvatar, setLastConfirmedAvatar] = useState<
    string | IconItem
  >(form.avatar_url);
  const [lastConfirmedOption, setLastConfirmedOption] = useState<string>("0");
  const [ngWords, setNgWords] = useState<GetNGWordsResponse[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClickActionButton = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };
  const [isShowAvatarDialog, setIsShowAvatarDialog] = useState(false);
  const [commonMemberIcons, setCommonMemberIcons] = useState<any>([]);
  const [selectedOption, setSelectedOption] = useState("0");
  const [selectedAvatar, setSelectedAvatar] = useState<string | IconItem>(
    form.avatar_url
  );
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(
    null
  );
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [isChange, setIsChange] = useState(false);
  const [
    isShowPushNotificationSettingModal,
    setIsShowPushNotificationSettingModal,
  ] = useState(false);

  const location = useLocation();
  const isUsingPwa = isPWA();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const fetchCommonIcons = async () => {
      const response = await getCommonMemberIcon();
      setCommonMemberIcons(response.data);
    };

    fetchCommonIcons();
    const fetchNgwordData = async () => {
      const respone = await fetchNGWords();
      setNgWords(respone.result ?? []);
    };
    fetchNgwordData();
  }, []);

  useEffect(() => {
    fetchMemberDetail().then((res: TApiResult & TApiMemberDetailResult) => {
      if (!res.isSuccess || !res.result) return history.push(routes.error.path);
      const newForm: TMemberEditForm = {
        ...initial,
        ...{
          tel: res.result.tel,
          email: res.result.email,
          birthday:
            res.result.birthday !== null
              ? new Date(res.result.birthday)
              : undefined,
          instagramOwnerinput: res.result.instagram_ownerinput,
          line: 0,
          mail_not_recommended: res.result.mail_not_recommended,
          line_not_recommended: res.result.line_not_recommended,
          allow_chat_mail_sending:
            res.result.allow_chat_mail_sending === 1 ? 0 : 1,
          nickname: res.result.nickname,
          avatar_url: res.result.avatar_url,
        },
      };
      setSelectedAvatar(res.result?.avatar_url);
      setIsShownUnlinkLine(res.result.link_user_line_account === 1);
      setForm(newForm);
      setName(`${res.result.sei}${res.result.mei}`);
      setNameKana(`${res.result.kana_sei}${res.result.kana_mei}`);
    });

    if (
      form.existPassword !== "" ||
      form.newPassword !== "" ||
      form.newPasswordConfirm !== ""
    )
      setPasswordFormOpen(true);
  }, []);

  useEffect(() => {
    if (!isChange) return;

    const newErrors = filterMessageFormError(
      "new_password_confirm" as TAppMemberEditFormItem,
      "パスワードが一致しません",
      formErrors
    );
    if (form.newPassword !== form.newPasswordConfirm) {
      newErrors.push({
        key: "new_password_confirm" as TAppMemberEditFormItem,
        messages: ["パスワードが一致しません"],
      });
    }
    setFormErrors(newErrors);
    setCanApply(
      validForm(form, passwordFormOpen, true) &&
        newErrors.filter((e) => e.messages.length !== 0).length === 0
    );
  }, [form]);

  const hundleChange = (newForm: TMemberEditForm): void => {
    setIsChange(true);
    setForm(newForm);
  };

  const isPasswordFormEmpty = (newForm: TMemberEditForm) => {
    return (
      newForm.existPassword === "" &&
      newForm.newPassword === "" &&
      newForm.newPasswordConfirm === ""
    );
  };

  const validForm = (
    newForm: TMemberEditForm,
    newPasswordFormOpen: boolean,
    newIsChange: boolean
  ): boolean => {
    const isValidMemberForm = newForm.email !== "" && newForm.tel !== "";
    const isValidPasswordForm =
      (!newPasswordFormOpen && isPasswordFormEmpty(newForm)) ||
      (newPasswordFormOpen &&
        newForm.existPassword !== "" &&
        newForm.newPassword !== "" &&
        newForm.newPassword === newForm.newPasswordConfirm &&
        CONSTANTS.PATTERN.PASSWORD.test(newForm.newPassword));
    return newIsChange && isValidMemberForm && isValidPasswordForm;
  };

  const handleNicknameChange = (e: TMemberEditForm) => {
    const value = e.nickname;
    const isHasNgWord =
      ngWords.filter(
        (item) =>
          item.word && value.toLowerCase().includes(item.word.toLowerCase())
      ).length > 0;
    if (isHasNgWord) {
      const newError: TAppFormError<TAppMemberEditFormItem> = {
        key: "nickname" as TAppMemberEditFormItem,
        messages: ["NGワードが含まれているため設定ができません。"],
      };
      setFormErrors([...formErrors, newError]);
    }
    setIsChange(true);
    setForm(e);
  };

  const validateAvatarUrl = (fileSize = 0) => {
    const clearOldAvatarErrorMessage = formErrors?.filter(
      (formError) => formError?.key !== ("avatar_url" as TAppMemberEditFormItem)
    );
    let cloneErrorMessage = [...clearOldAvatarErrorMessage];
    if (fileSize > MAX_FILE_SIZE) {
      const uploadAvatarUrlErrorMessage: TAppFormError<TAppMemberEditFormItem> =
        {
          key: "avatar_url" as TAppMemberEditFormItem,
          messages: ["ファイルサイズは10MB以下の画像を指定してください。"],
        };
      cloneErrorMessage = [...cloneErrorMessage, uploadAvatarUrlErrorMessage];
    }
    setFormErrors(cloneErrorMessage);
  };

  const hundleSubmit = async (): Promise<void> => {
    window.scrollTo(0, 0);
    if (!validForm(form, passwordFormOpen, isChange)) return;

    let finalAvatarUrl = form.avatar_url;

    if (form.avatarType === "upload") {
      if (selectedAvatarFile) {
        const formData = new FormData();
        const location = `${subdomain}/images/message-board`;
        formData.append("picture", selectedAvatarFile);
        formData.append("location", location);
        try {
          const response = await postImage(formData);
          finalAvatarUrl = response?.data?.file_url || "";
        } catch (error) {
          throw new Error("画像のアップロードに失敗しました");
        }
      } else if (form.avatar_url.startsWith("blob:")) {
        finalAvatarUrl = "";
      } else {
        finalAvatarUrl = form.avatar_url;
      }
    } else if (form.avatarType === "icon") {
      finalAvatarUrl =
        typeof selectedAvatar === "object"
          ? selectedAvatar.file_url
          : selectedAvatar || form.avatar_url;
    }

    const { newPasswordConfirm, ...formParam } = form;
    const params = {
      ...formParam,
      avatar_url: finalAvatarUrl,
    };

    postMemberUpdate(params).then(
      async (res: TApiResult & TApiMemberUpdateValidateErrors) => {
        if (!res.isSuccess) {
          setFormErrors(res.validateErrors);
        } else {
          const updated = await fetchMemberDetail();
          if (!updated.isSuccess || !updated.result)
            return history.push(routes.error.path);

          setForm((prevForm) => ({
            ...prevForm,
          }));

          setSelectedAvatar(updated.result?.avatar_url);
          setIsUpdated(true);
          postMemberAccessLog(ACCESS_LOG_ACTION.memberEdit, document.referrer);
        }
      }
    );
  };

  const hundleClick = (): void => {
    if (passwordFormOpen) {
      setTmpPassword({
        existPassword: form.existPassword,
        newPassword: form.newPassword,
        newPasswordConfirm: form.newPasswordConfirm,
      });
      const newForm = {
        ...form,
        ...{
          existPassword: "",
          newPassword: "",
          newPasswordConfirm: "",
        },
      };
      setForm(newForm);
      setCanApply(validForm(newForm, !passwordFormOpen, isChange));
    } else {
      const newForm = {
        ...form,
        ...tmpPassword,
      };
      setForm(newForm);
      setCanApply(validForm(newForm, !passwordFormOpen, isChange));
    }
    setPasswordFormOpen(!passwordFormOpen);
  };

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
    setForm((prev) => ({
      ...prev,
      avatarType: value === "1" ? "upload" : value === "2" ? "icon" : "none",
    }));
    if (value === "1") {
      uploadRef.current?.click();
    } else {
      setIsShowAvatarDialog(true);
    }
  };

  return (
    <>
      <div
        className={`${STMemberEditForm.member_edit_form} ${STUtils.container_form}`}
      >
        <Helmet>
          <meta http-equiv="cache-control" content="no-cache" />
        </Helmet>
        <h1 className={STMemberEditForm.member_edit_form_title}>
          会員情報変更
        </h1>
        <div className={STMemberEditForm.member_edit_form_form}>
          {isUpdated && (
            <AlartMessageBox message="会員情報が更新されました。" />
          )}

          <div className={STMemberEditForm.avatar_section}>
            <div className={STMemberEditForm.avatar_wrapper}>
              <div className={STMemberEditForm.avatar_image}>
                {form.avatar_url || selectedAvatar ? (
                  <img
                    src={
                      selectedOption === "2" &&
                      typeof selectedAvatar === "object"
                        ? selectedAvatar.presign_url ?? selectedAvatar.file_url
                        : typeof selectedAvatar === "string"
                        ? selectedAvatar
                        : ""
                    }
                    alt="avatar_url"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <AccountIcon width={60} height={60} />
                )}
              </div>
              <div>
                <button
                  type="button"
                  className={`${STMemberEditForm.uploadButton}`}
                  onClick={handleClickActionButton}
                >
                  アイコン画像を選択
                </button>
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <div className={STMemberEditForm.avatar_option_container}>
                    {[
                      { title: "画像をアップロード", value: "1" },
                      { title: "アイコンから選択", value: "2" },
                    ].map((action) => (
                      <p
                        key={action.value}
                        onClick={() => {
                          handleOptionChange(action.value);
                          setAnchorEl(null);
                        }}
                      >
                        {action.title}
                      </p>
                    ))}
                  </div>
                </Popover>
              </div>
              <div className={STMemberEditForm.avatar_note}>
                <div>推奨画像サイズ 512×512以下</div>
                <div>最大サイズ：10MB</div>
              </div>
              {AlartMessages({
                title: "アバター画像のURL",
                errors: formErrors,
                keys: [MEMBER_EDIT_FORM_ITEM.avatar_url],
              })}
            </div>
          </div>
          <input
            accept="image/*"
            type="file"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const selectedFiles: FileList | null = e?.target?.files;
              if (!selectedFiles?.length) {
                setSelectedAvatar(lastConfirmedAvatar);
                setForm((prev) => ({
                  ...prev,
                  avatar_url:
                    typeof lastConfirmedAvatar === "string"
                      ? lastConfirmedAvatar
                      : lastConfirmedAvatar?.file_url ?? "",
                  avatarType:
                    lastConfirmedOption === "1"
                      ? "upload"
                      : lastConfirmedOption === "2"
                      ? "icon"
                      : "none",
                }));
                setSelectedOption(lastConfirmedOption);
                return;
              }

              const file = selectedFiles[0];
              validateAvatarUrl(file.size);
              const previewUrl = URL.createObjectURL(file);
              setIsChange(true);
              setForm({
                ...form,
                avatar_url: previewUrl,
                avatarType: "upload",
              });
              setSelectedAvatarFile(file);
              setSelectedAvatar(previewUrl);
              setSelectedOption("1");
              setLastConfirmedAvatar(previewUrl);
              setLastConfirmedOption("1");
            }}
            style={{ display: "none" }}
            ref={uploadRef}
          />
          <table className={STMemberEditForm.member_edit_form_table}>
            <tr>
              <th>名前</th>
              <td className={STMemberEditForm.member_edit_form_text}>{name}</td>
            </tr>
            <tr>
              <th>ふりがな</th>
              <td className={STMemberEditForm.member_edit_form_text}>
                {nameKana}
              </td>
            </tr>
            <SingleTextRow<TAppMemberEditFormItem>
              defaultValue={form.tel}
              title="電話番号"
              formKey={MEMBER_EDIT_FORM_ITEM.tel}
              errors={formErrors}
              setErrors={setFormErrors}
              hundleOnChange={(input) => hundleChange({ ...form, tel: input })}
              pattern={CONSTANTS.PATTERN.ONLY_NUMBER}
              patternText="半角数字"
              maxLength={11}
              minLength={10}
              required={true}
            />
            <SingleTextRow<TAppMemberEditFormItem>
              defaultValue={form.email}
              title="メールアドレス"
              formKey={MEMBER_EDIT_FORM_ITEM.email}
              errors={formErrors}
              setErrors={setFormErrors}
              hundleOnChange={(input) =>
                hundleChange({ ...form, email: input })
              }
              maxLength={255}
              minLength={3}
              pattern={EMAIL_PATTERN}
              patternText="メールアドレスの形式"
              required={true}
            />
            <SingleTextRow<TAppMemberEditFormItem>
              defaultValue={form.nickname}
              title="ニックネーム"
              formKey={MEMBER_EDIT_FORM_ITEM.nickname}
              errors={formErrors}
              setErrors={setFormErrors}
              hundleOnChange={(input) =>
                handleNicknameChange({ ...form, nickname: input })
              }
              maxLength={20}
            />
            <MultiPullDownRow<TAppMemberEditFormItem>
              defaultValue={form.birthday}
              title="お誕生日"
              formKey={MEMBER_EDIT_FORM_ITEM.birthday}
              errors={formErrors}
              setErrors={setFormErrors}
              hundleOnChange={(input) =>
                hundleChange({ ...form, birthday: input })
              }
              options={{ years: years, months: months, days: days }}
            />
            <SingleTextWithIconRow<TAppMemberEditFormItem>
              defaultValue={form.instagramOwnerinput}
              title="Instagram"
              formKey={MEMBER_EDIT_FORM_ITEM.instagramOwnerinput}
              errors={formErrors}
              setErrors={setFormErrors}
              pattern={CONSTANTS.PATTERN.INSTAGRAM}
              patternText="記号は「. _」のみ"
              maxLength={30}
              autoComplete="new-password"
              hundleOnChange={(input) =>
                hundleChange({ ...form, instagramOwnerinput: input })
              }
            />
            {isShownUnlinkLine && (
              <SingleCheckBoxRow<TAppMemberEditFormItem>
                defaultValue={form.line}
                title="LINE連携"
                label="LINE連携を解除する"
                formKey={MEMBER_EDIT_FORM_ITEM.line}
                errors={formErrors}
                setErrors={setFormErrors}
                hundleOnChange={(input: number) =>
                  hundleChange({ ...form, line: input })
                }
              />
            )}
            <TableRow title="お知らせ">
              <div>
                <SingleCheckBox<TAppMemberEditFormItem>
                  defaultValue={form.mail_not_recommended}
                  label="メール送信案内を希望しない"
                  formKey={MEMBER_EDIT_FORM_ITEM.mail_not_recommended}
                  errors={formErrors}
                  setErrors={setFormErrors}
                  hundleOnChange={(input: number) =>
                    hundleChange({ ...form, mail_not_recommended: input })
                  }
                />
                <SingleCheckBox<TAppMemberEditFormItem>
                  defaultValue={form.allow_chat_mail_sending}
                  label="チャット受信時のメール通知を受信しない"
                  formKey={MEMBER_EDIT_FORM_ITEM.allow_chat_mail_sending}
                  errors={formErrors}
                  setErrors={setFormErrors}
                  hundleOnChange={(input: number) =>
                    hundleChange({ ...form, allow_chat_mail_sending: input })
                  }
                />
              </div>
              {isShownUnlinkLine && (
                <div style={{ marginTop: "10px" }}>
                  <SingleCheckBox<TAppMemberEditFormItem>
                    defaultValue={form.line_not_recommended}
                    label="LINE送信案内を希望しない"
                    formKey={MEMBER_EDIT_FORM_ITEM.line_not_recommended}
                    errors={formErrors}
                    setErrors={setFormErrors}
                    hundleOnChange={(input: number) =>
                      hundleChange({ ...form, line_not_recommended: input })
                    }
                  />
                </div>
              )}
            </TableRow>
            {isMobile && isUsingPwa && (
              <Button
                className={STMemberEditForm.push_notification_btn}
                clickAction={() => setIsShowPushNotificationSettingModal(true)}
                text={
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <NotificationsNone fontSize="small" />
                    プッシュ通知を設定する
                  </div>
                }
              />
            )}
          </table>
          <div
            className={
              passwordFormOpen
                ? `${STMemberEditForm.member_edit_form_change_password_btn_open}`
                : `${STMemberEditForm.member_edit_form_change_password_btn}`
            }
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              hundleClick();
            }}
          >
            パスワードを変更する
          </div>
        </div>
        <table
          className={`${passwordFormOpen ? "" : STUtils.dn} ${
            STMemberEditForm.member_edit_form_table
          }`}
        >
          <SinglePasswordRow<TAppMemberEditFormItem>
            defaultValue={form.existPassword}
            title="現在のパスワード"
            formKey={MEMBER_EDIT_FORM_ITEM.existPassword}
            errors={formErrors}
            setErrors={setFormErrors}
            hundleOnChange={(input) =>
              hundleChange({ ...form, existPassword: input })
            }
            maxLength={20}
            placeholder="現在のパスワード"
            minLength={8}
            autoComplete="new-password"
            pattern={CONSTANTS.PATTERN.PASSWORD}
            patternText="英数字記号"
          />
          <SinglePasswordRow<TAppMemberEditFormItem>
            defaultValue={form.newPassword}
            title="変更するパスワード"
            placeholder="パスワード8桁以上の英数字"
            formKey={MEMBER_EDIT_FORM_ITEM.newPassword}
            errors={formErrors}
            setErrors={setFormErrors}
            hundleOnChange={(input) =>
              hundleChange({ ...form, newPassword: input })
            }
            maxLength={20}
            minLength={8}
            autoComplete="new-password"
            pattern={CONSTANTS.PATTERN.PASSWORD}
            patternText="英数字記号"
          />
          <SinglePasswordRow<TAppMemberEditFormItem>
            defaultValue={form.newPasswordConfirm}
            title="変更するパスワード"
            subTitle="（確認）"
            placeholder="パスワード再入力"
            formKey={MEMBER_EDIT_FORM_ITEM.newPasswordConfirm}
            errors={formErrors}
            setErrors={setFormErrors}
            hundleOnChange={(input) =>
              hundleChange({ ...form, newPasswordConfirm: input })
            }
            maxLength={20}
            minLength={8}
            autoComplete="new-password"
          />
        </table>
        <Button
          className={
            canApply
              ? `${STMemberEditForm.apply_btn} ${STButton.primary}`
              : `${STMemberEditForm.apply_btn} ${STButton.disable}`
          }
          clickAction={hundleSubmit}
          text="変更"
          disabled={!canApply}
        />

        {isShowPushNotificationSettingModal && (
          <PushNotificationSettingModal
            onClose={() => setIsShowPushNotificationSettingModal(false)}
          />
        )}
      </div>
      <AvatarSelectDialog
        open={isShowAvatarDialog}
        onClose={(newAvatar: string, isChange: boolean) => {
          validateAvatarUrl(0);
          if (isChange) {
            setSelectedAvatar(newAvatar);
            setIsChange(true);
            setForm({
              ...form,
              avatar_url: newAvatar,
              avatarType: "icon",
            });
            setLastConfirmedAvatar(newAvatar);
            setLastConfirmedOption("2");
          } else {
            setSelectedAvatar(lastConfirmedAvatar);
            setForm({
              ...form,
              avatar_url:
                typeof lastConfirmedAvatar === "string"
                  ? lastConfirmedAvatar
                  : lastConfirmedAvatar?.file_url ?? "",
              avatarType:
                lastConfirmedOption === "1"
                  ? "upload"
                  : lastConfirmedOption === "2"
                  ? "icon"
                  : "none",
            });
            setSelectedOption(lastConfirmedOption);
          }
          setIsShowAvatarDialog(false);
        }}
        icons={commonMemberIcons}
      />
    </>
  );
};

export default MemberEditForm;
